import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, X, Bell } from 'lucide-react';
import { notificationPreferenceService } from '../../lib/services.notification';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface NotificationPreferenceWithPatient {
  id: string;
  patient_id: string;
  patient_name: string;
  patient_unique_id: string;
  enable_sms: boolean;
  enable_email: boolean;
  sms_number: string | null;
  email_address: string | null;
  created_at: string;
  updated_at: string;
}

interface Patient {
  id: string;
  name: string;
  unique_id: string;
  phone?: string;
  email?: string;
}

const NotificationPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferenceWithPatient[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPreference, setEditingPreference] = useState<NotificationPreferenceWithPatient | null>(null);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    enable_sms: true,
    enable_email: true,
    sms_number: '',
    email_address: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: prefsData, error: prefsError } = await supabase
        .from('notification_preferences')
        .select(`
          *,
          patient:patients(id, name, unique_id)
        `)
        .order('created_at', { ascending: false });

      if (prefsError) throw prefsError;

      const mapped = (prefsData || []).map((pref: any) => ({
        ...pref,
        patient_name: pref.patient?.name || 'Unknown',
        patient_unique_id: pref.patient?.unique_id || '-',
      }));

      setPreferences(mapped);

      const { data: patientsData, error: patientsError } = await supabase
        .from('patients')
        .select('id, name, unique_id, phone, email')
        .order('name', { ascending: true });

      if (patientsError) throw patientsError;
      setPatients(patientsData || []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await notificationPreferenceService.createOrUpdate(formData.patient_id, {
        enable_sms: formData.enable_sms,
        enable_email: formData.enable_email,
        sms_number: formData.sms_number || null,
        email_address: formData.email_address || null,
      });

      toast.success(editingPreference ? 'Preferences updated successfully' : 'Preferences created successfully');
      setShowModal(false);
      setEditingPreference(null);
      resetForm();
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleEdit = (preference: NotificationPreferenceWithPatient) => {
    setEditingPreference(preference);
    setFormData({
      patient_id: preference.patient_id,
      enable_sms: preference.enable_sms,
      enable_email: preference.enable_email,
      sms_number: preference.sms_number || '',
      email_address: preference.email_address || '',
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingPreference(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      enable_sms: true,
      enable_email: true,
      sms_number: '',
      email_address: '',
    });
  };

  const handlePatientChange = (patientId: string) => {
    setFormData({ ...formData, patient_id: patientId });
    
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData(prev => ({
        ...prev,
        patient_id: patientId,
        sms_number: patient.phone || '',
        email_address: patient.email || '',
      }));
    }
  };

  const filteredPreferences = preferences.filter((pref) =>
    pref.patient_name.toLowerCase().includes(search.toLowerCase()) ||
    pref.patient_unique_id.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Notification Preferences</h1>
            <p className="text-gray-600">Manage patient notification settings for SMS and Email</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Plus className="w-4 h-4" />
            <span>Add Preferences</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search patients..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMS Enabled
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Enabled
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMS Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPreferences.map((preference) => (
                <tr key={preference.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-blue-600">{preference.patient_unique_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{preference.patient_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {preference.enable_sms ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-red-600">✗</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {preference.enable_email ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-red-600">✗</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {preference.sms_number || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {preference.email_address || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(preference)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingPreference ? 'Edit Notification Preferences' : 'Add Notification Preferences'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Patient *</label>
                <select
                  value={formData.patient_id}
                  onChange={(e) => handlePatientChange(e.target.value)}
                  disabled={!!editingPreference}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select Patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.unique_id} - {patient.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enable_sms"
                  checked={formData.enable_sms}
                  onChange={(e) => setFormData({ ...formData, enable_sms: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enable_sms" className="text-sm font-medium text-gray-700">
                  Enable SMS Notifications
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">SMS Number</label>
                <input
                  type="tel"
                  value={formData.sms_number}
                  onChange={(e) => setFormData({ ...formData, sms_number: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1234567890"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enable_email"
                  checked={formData.enable_email}
                  onChange={(e) => setFormData({ ...formData, enable_email: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enable_email" className="text-sm font-medium text-gray-700">
                  Enable Email Notifications
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                <input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="patient@example.com"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  {editingPreference ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;
